// Add or change a registration journal: its name, kind, how its numbers are
// built (with a live preview) and whether the sequence restarts each year.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors.js';
import { FieldError, REQUIRED_LABEL } from '@/shared/ui/FieldError.jsx';
import { PATTERN_MAX, REGISTER_KINDS, formatNumber, patternProblem } from '../lib/journals.js';
import { LIMITS } from '../lib/validation.js';

const FIELDS = ['name', 'kind', 'pattern', 'reset', 'note'];

function journalProblems(form, t) {
  const out = {};
  const name = form.name.trim();
  if (!name) out.name = t('errors:fields.required');
  else if (name.length > LIMITS.journalName) out.name = t('errors:fields.tooLong', { max: LIMITS.journalName });
  if (form.note.trim().length > LIMITS.note) out.note = t('errors:fields.tooLong', { max: LIMITS.note });
  return out;
}

const PRESETS = ['{seq}', '{case}/{seq}', '{seq}-{yy}', '{year}/{seq:4}'];

export function JournalDialog({ open, onOpenChange, initial, onSave }) {
  const { t } = useTranslation('records');
  const [form, setForm] = useState({ name: '', kind: 'incoming', pattern: '{seq}', reset: 'yearly', note: '' });
  const [busy, setBusy] = useState(false);
  const problem = patternProblem(form.pattern);
  const checks = useFieldErrors(journalProblems(form, t));
  const { reset: resetChecks } = checks;

  useEffect(() => {
    if (!open) return;
    resetChecks();
    setForm({
      name: initial?.name || '',
      kind: initial?.kind || 'incoming',
      pattern: initial?.pattern || '{seq}',
      reset: initial?.reset || 'yearly',
      note: initial?.note || '',
    });
  }, [open, initial, resetChecks]);

  const set = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    Object.keys(patch).forEach((key) => checks.touch(key));
  };
  const year = new Date().getFullYear();

  const submit = async () => {
    if (!checks.attempt() || problem) return;
    setBusy(true);
    try {
      const res = await onSave({ ...form, name: form.name.trim(), pattern: form.pattern.trim(), note: form.note.trim() || null });
      if (res && !res.success) checks.showServer(res, FIELDS);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(initial ? 'journal.editTitle' : 'journal.newTitle')}</DialogTitle>
          <DialogDescription>{t('journal.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="journal-name" className={REQUIRED_LABEL}>{t('journal.name')}</Label>
            <Input id="journal-name" required maxLength={LIMITS.journalName} value={form.name}
              onChange={(e) => set({ name: e.target.value })} {...checks.describe('name', 'journal')} />
            <FieldError id="journal-name-error" message={checks.errors.name} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t('journal.kind')}</Label>
              <Select value={form.kind} onValueChange={(kind) => set({ kind })}>
                <SelectTrigger aria-label={t('journal.kind')}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REGISTER_KINDS.map((k) => <SelectItem key={k} value={k}>{t(`journal.kinds.${k}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('journal.reset')}</Label>
              <Select value={form.reset} onValueChange={(reset) => set({ reset })}>
                <SelectTrigger aria-label={t('journal.reset')}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yearly">{t('journal.resets.yearly')}</SelectItem>
                  <SelectItem value="never">{t('journal.resets.never')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="journal-pattern" className={REQUIRED_LABEL}>{t('journal.pattern')}</Label>
            <Input id="journal-pattern" className="font-mono" required maxLength={PATTERN_MAX} value={form.pattern}
              onChange={(e) => set({ pattern: e.target.value })} {...checks.describe('pattern', 'journal')} />
            <div className="flex flex-wrap gap-1">
              {PRESETS.map((p) => (
                <button key={p} type="button" onClick={() => set({ pattern: p })} className="rounded border px-1.5 py-0.5 font-mono text-xs hover:bg-muted">
                  {p}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{t('journal.patternHint')}</p>
            {problem ? (
              <p className="text-xs text-red-600 dark:text-red-400" data-testid="journal-pattern-problem">{t(`journal.problems.${problem}`)}</p>
            ) : (
              <p className="text-xs" data-testid="journal-preview">
                {t('journal.preview', { number: formatNumber(form.pattern, { seq: 1, year, caseIndex: '01-05' }) })}
              </p>
            )}
            <FieldError id="journal-pattern-error" message={checks.errors.pattern} />
            {initial && initial.pattern !== form.pattern && (
              <p className="text-xs text-amber-700 dark:text-amber-300">{t('journal.patternChange')}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="journal-note">{t('journal.note')}</Label>
            <Textarea id="journal-note" rows={2} maxLength={LIMITS.note} value={form.note}
              onChange={(e) => set({ note: e.target.value })} {...checks.describe('note', 'journal')} />
            <FieldError id="journal-note-error" message={checks.errors.note} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('actions.cancel')}</Button>
          <Button onClick={submit} disabled={busy || !form.name.trim() || Boolean(problem) || checks.blocked} data-testid="journal-save">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
